import type { ReactNode } from 'react';
import { Badge, Button, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import GaisukeArenaMark from '../systems/gaisuke/GaisukeArenaMark';
import {
  getKensukeFeatureCardsForSurface,
  type KensukeAccessBadge,
  type KensukeFeatureCardDef,
  type KensukeFeatureGroup,
} from '../../constants/kensukeFeatureCards';
import { RS_FEATURED_PUBLIC_BOARD_URL } from '../../constants/reservationFeaturedBoard.config';
import { gaisukePublicPath } from '../../utils/gaisukePath';
import { keibaArenaPublicPath } from '../../utils/keibaArenaPath';
import { banquetPublicPath } from '../../utils/banquetPath';
import { econReportPublicPath } from '../../utils/econReportPath';
import { gaisukeFxPublicPath } from '../../utils/gaisukeFxPath';
import { fetchGaisukeArenaPublicLatest } from '../../services/systems/gaisuke/gaisuke.service';
import { youtubeWatchUrl } from '../../utils/youtubeEmbed';
import { laboSukePublicPath } from '../../utils/laboSukePath';
import { keisukePublicPath } from '../../utils/keisukePath';
import api from '../../services/api';
import { useAiContentHighlights } from '../../hooks/useAiContentHighlights';
import styles from './KensukeSurface.module.scss';
import { featureEntranceService, recordFeatureEntrance } from '../../services/featureEntrance.service';
import {
  featureEntranceRankMap,
  sortKensukeFeatureCardsByPopularity,
} from '../../utils/sortKensukeFeatureCardsByPopularity';
import type { KensukeFeatureCardId } from '../../constants/kensukeFeatureCards';

type FeatureSurface = 'home' | 'dashboard';

interface KensukeFeatureGridProps {
  surface: FeatureSurface;
  isLoggedIn: boolean;
  isAdmin: boolean;
  /** 指定するとその区分（AIコンテンツ / ツール）のカードだけを並べる */
  group?: KensukeFeatureGroup;
}

const ACCESS_BADGE_UI: Record<
  KensukeAccessBadge,
  { key: string; bg: string; defaultValue: string }
> = {
  demo: { key: 'home.feature_demo_badge', bg: 'success', defaultValue: 'デモ' },
  public: { key: 'home.feature_public_badge', bg: 'success', defaultValue: 'ログイン不要' },
  login_required: {
    key: 'home.feature_login_required_badge',
    bg: 'secondary',
    defaultValue: 'ログイン必須',
  },
};

type TileAction =
  | { kind: 'link'; to: string; label: string; variant?: string; ariaLabel?: string }
  | {
      kind: 'href';
      href: string;
      label: string;
      variant?: string;
      ariaLabel?: string;
      target?: string;
      /** false のとき入口カウントに足さない（YouTube 視聴はツール入口ではない） */
      trackEntrance?: boolean;
    };

type Translate = (key: string, options?: object) => string;

function FeatureIcon({ def }: { def: KensukeFeatureCardDef }) {
  const { t } = useTranslation();
  if (def.icon === 'gaisuke') {
    return (
      <div className={styles.tileIcon} aria-hidden>
        <GaisukeArenaMark size={28} title={t(def.titleKey)} />
      </div>
    );
  }
  return (
    <div className={styles.tileIcon} style={{ color: def.accentColor }} aria-hidden>
      {def.emoji}
    </div>
  );
}

function reservationCloudLoginAction(t: Translate, toolName: string): TileAction {
  return {
    kind: 'link',
    to: '/login?next=/reservations',
    label: t('home.feature_sample_cloud_cta', { defaultValue: 'クラウドDB（ログイン必須）' }),
    ariaLabel: t('home.feature_login_named', {
      name: toolName,
      defaultValue: '{{name}}を使うにはログイン',
    }),
    variant: 'outline-primary',
  };
}

function demoBoardAction(t: Translate, toolName: string): TileAction {
  return {
    kind: 'href',
    href: RS_FEATURED_PUBLIC_BOARD_URL,
    label: t('home.feature_reservation_public_cta', { defaultValue: 'デモ' }),
    ariaLabel: t('home.feature_reservation_demo_named', {
      name: toolName,
      defaultValue: '{{name}}のデモを開く',
    }),
    variant: 'outline-success',
  };
}

type HomeOpenLabel = { key: string; defaultValue: string };

/** ホームはローカル正本の種別を短く明示。ダッシュボードは従来の長い CTA。 */
function openToolAction(
  to: string,
  t: Translate,
  surface: FeatureSurface,
  title: string,
  dashKey: string,
  dashDefault: string,
  homeLabel?: HomeOpenLabel
): TileAction {
  if (surface === 'home') {
    const label = homeLabel
      ? t(homeLabel.key, { defaultValue: homeLabel.defaultValue })
      : t('home.feature_open_cta', { defaultValue: '開く' });
    return {
      kind: 'link',
      to,
      label,
      ariaLabel: t('home.feature_open_named', {
        name: title,
        defaultValue: '{{name}}を開く',
      }),
    };
  }
  return { kind: 'link', to, label: t(dashKey, { defaultValue: dashDefault }) };
}

const HOME_ZIP_CTA: HomeOpenLabel = {
  key: 'home.feature_local_zip_cta',
  defaultValue: '.zip（ログイン不要）で開く',
};
const HOME_KEISUKE_CTA: HomeOpenLabel = {
  key: 'home.feature_local_keisuke_cta',
  defaultValue: '.keisuke（ログイン不要）で開く',
};
const HOME_GPROJ_CTA: HomeOpenLabel = {
  key: 'home.feature_local_gproj_cta',
  defaultValue: '.gproj（ログイン不要）で開く',
};

function weeklyVideoAction(t: Translate, watchUrl: string | null): TileAction | null {
  if (!watchUrl) return null;
  return {
    kind: 'href',
    href: watchUrl,
    label: t('home.feature_gaisuke_video_cta', { defaultValue: '今週の動画を見る' }),
    ariaLabel: t('home.feature_gaisuke_video_cta', { defaultValue: '今週の動画を見る' }),
    variant: 'outline-danger',
    target: '_blank',
    trackEntrance: false,
  };
}

function resolveActions(
  def: KensukeFeatureCardDef,
  opts: {
    surface: FeatureSurface;
    isLoggedIn: boolean;
    lang: string;
    t: Translate;
    weeklyVideoUrl: string | null;
  }
): TileAction[] {
  const { surface, isLoggedIn, lang, t, weeklyVideoUrl } = opts;
  const guest = surface === 'home' && !isLoggedIn;
  const title = t(def.titleKey);

  switch (def.id) {
    case 'keisuke':
      return [
        openToolAction(
          keisukePublicPath(lang),
          t,
          surface,
          title,
          'home.feature_keisuke_open_cta',
          '計助を開く',
          HOME_KEISUKE_CTA
        ),
      ];
    case 'sample':
      if (guest) {
        return [
          {
            kind: 'link',
            to: '/samples',
            label: t('home.feature_sample_zip_cta', { defaultValue: '.zip（ログイン不要）で開く' }),
            ariaLabel: t('home.feature_sample_zip_aria', {
              defaultValue: 'サンプル管理をローカル Zip（ログイン不要）で開く',
            }),
          },
          {
            kind: 'link',
            to: '/login?next=/samples&sampleSource=db',
            label: t('home.feature_sample_cloud_cta', {
              defaultValue: 'クラウドDB（ログイン必須）',
            }),
            ariaLabel: t('home.feature_sample_cloud_aria', {
              defaultValue: 'ログインしてサンプル管理のクラウド DB を使う',
            }),
            variant: 'outline-primary',
          },
        ];
      }
      return [
        { kind: 'link', to: '/samples/dashboard', label: t('dashboard.access_sample_dashboard') },
        {
          kind: 'link',
          to: '/samples',
          label: t('dashboard.access_sample_management'),
          variant: 'outline-primary',
        },
      ];
    case 'reservation':
      // 本利用はログイン必須。デモは副次。
      if (guest) return [demoBoardAction(t, title), reservationCloudLoginAction(t, title)];
      return [
        {
          kind: 'link',
          to: '/reservations',
          label: t('dashboard.access_reservation', { defaultValue: '予約システムを開く' }),
        },
        demoBoardAction(t, title),
      ];
    case 'transit_pass':
      return [
        openToolAction(
          '/transit-pass-pool',
          t,
          surface,
          title,
          'dashboard.access_transit_pass_manage',
          '交通IC・共用カードを開く',
          HOME_ZIP_CTA
        ),
      ];
    case 'labo_suke':
      return [
        openToolAction(
          laboSukePublicPath(lang),
          t,
          surface,
          title,
          'dashboard.access_labo_suke',
          'ラボ助を開く',
          HOME_ZIP_CTA
        ),
      ];
    case 'gaisuke': {
      const open = openToolAction(
        gaisukePublicPath(lang),
        t,
        surface,
        title,
        'home.feature_gaisuke_cta',
        'AIトレードアリーナを開く'
      );
      const video = weeklyVideoAction(t, weeklyVideoUrl);
      return video ? [open, video] : [open];
    }
    case 'gaisuke_fx': {
      const open = openToolAction(
        gaisukeFxPublicPath(lang),
        t,
        surface,
        title,
        'home.feature_gaisuke_fx_cta',
        'FXドル円編を開く'
      );
      const video = weeklyVideoAction(t, weeklyVideoUrl);
      return video ? [open, video] : [open];
    }
    case 'keiba_arena':
      return [
        openToolAction(
          keibaArenaPublicPath(lang),
          t,
          surface,
          title,
          'home.feature_keiba_arena_cta',
          'AI競馬アリーナを開く'
        ),
      ];
    case 'banquet':
      return [
        openToolAction(
          banquetPublicPath(lang),
          t,
          surface,
          title,
          'home.feature_banquet_cta',
          '熱狂の宴を開く'
        ),
      ];
    case 'econ_report':
      return [
        openToolAction(
          econReportPublicPath(lang),
          t,
          surface,
          title,
          'home.feature_econ_report_cta',
          'AI投資情報を開く'
        ),
      ];
    case 'gene_analysis':
      return [
        openToolAction(
          '/gene-analysis',
          t,
          surface,
          title,
          'dashboard.access_gene_analysis',
          '遺伝子解析ツールを開く',
          HOME_GPROJ_CTA
        ),
      ];
    default:
      return [];
  }
}

function renderAction(action: TileAction, key: string, featureId: KensukeFeatureCardId): ReactNode {
  const variant = action.variant ?? 'primary';
  const onEntrance = () => recordFeatureEntrance(featureId);
  if (action.kind === 'href') {
    return (
      <Button
        key={key}
        as="a"
        href={action.href}
        variant={variant}
        size="sm"
        aria-label={action.ariaLabel}
        rel="noopener noreferrer"
        target={action.target}
        onClick={(e) => {
          if (action.trackEntrance !== false) onEntrance();
          e.stopPropagation();
        }}
      >
        {action.label}
      </Button>
    );
  }
  return (
    <Button
      key={key}
      as={Link as any}
      to={action.to}
      variant={variant}
      size="sm"
      aria-label={action.ariaLabel}
      onClick={() => onEntrance()}
    >
      {action.label}
    </Button>
  );
}

function FeatureTile({
  def,
  actions,
  surface,
  highlight,
}: {
  def: KensukeFeatureCardDef;
  actions: TileAction[];
  surface: FeatureSurface;
  /** AIコンテンツの「今の見どころ」1行（取得できたときだけ） */
  highlight?: string;
}) {
  const { t } = useTranslation();
  const isHome = surface === 'home';
  // トップはログイン済みでも「ログイン不要／必須」を見せる（どのカードが何を要るかが一目で分かる）
  return (
    <article className={styles.tile} style={{ ['--tile-accent' as string]: def.accentColor }}>
      <div className={styles.tileBody}>
        <div className={styles.tileHead}>
          <FeatureIcon def={def} />
          <div className={styles.tileTitleBlock}>
            <h3 className={styles.tileTitle}>{t(def.titleKey)}</h3>
            {isHome ? (
              <div className={styles.tileBadge}>
                {def.accessBadges.map((kind) => {
                  const badge = ACCESS_BADGE_UI[kind];
                  return (
                    <Badge key={kind} bg={badge.bg} pill>
                      {t(badge.key, { defaultValue: badge.defaultValue })}
                    </Badge>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        {def.noteKey ? (
          <div className={`${styles.tileBadge} mb-2`}>
            <Badge bg="primary" pill>
              {t(def.noteKey)}
            </Badge>
          </div>
        ) : null}

        {highlight ? (
          <p className={styles.tileHighlight}>
            <span className={styles.tileHighlightDot} aria-hidden />
            <span className={styles.tileHighlightText}>{highlight}</span>
          </p>
        ) : null}

        {isHome ? <p className={styles.tileDesc}>{t(def.descriptionKey)}</p> : null}

        {isHome && def.homeBulletKeys && def.homeBulletKeys.length > 0 ? (
          <ul className={styles.tileBullets}>
            {def.homeBulletKeys.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        ) : null}

        {actions.length > 0 ? (
          <div className={styles.tileActions}>
            <div className="d-grid gap-2">
              {actions.map((action, i) => renderAction(action, `${def.id}-${i}`, def.id))}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default function KensukeFeatureGrid({
  surface,
  isLoggedIn,
  isAdmin,
  group,
}: KensukeFeatureGridProps) {
  const { t, i18n } = useTranslation();
  const { data: keibaUi } = useQuery({
    queryKey: ['keiba-arena-ui-visibility'],
    queryFn: async () => {
      const res = await api.get<{ success?: boolean; showFeatureCard?: boolean }>(
        '/keiba-arena/public/ui-visibility'
      );
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  const { data: entranceRanks } = useQuery({
    queryKey: ['feature-entrance-ranks'],
    queryFn: () => featureEntranceService.getRanks(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
  const baseCards = getKensukeFeatureCardsForSurface(surface, {
    isLoggedIn,
    isAdmin,
    hideKeibaArenaFeatureCard: keibaUi?.showFeatureCard === false,
    group,
  });
  const rankMap = featureEntranceRankMap(entranceRanks ?? []);
  const cards = sortKensukeFeatureCardsByPopularity(baseCards, rankMap);
  const translate = t as Translate;
  const highlights = useAiContentHighlights(group === 'ai');
  const videoLang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'ja';
  const { data: latestVideo } = useQuery({
    queryKey: ['gaisuke-arena-public-latest', videoLang],
    queryFn: () => fetchGaisukeArenaPublicLatest(videoLang),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
  const weeklyVideoUrl = youtubeWatchUrl(
    videoLang === 'en'
      ? latestVideo?.youtubeVideoIdEn || latestVideo?.youtubeVideoIdJa
      : latestVideo?.youtubeVideoIdJa
  );

  /** 広い画面は3列（Bootstrap lg=4）。AI もツールも同じ。並びは page_view 人気順 */
  const colSpan = 4;

  return (
    <Row className="g-3">
      {cards.map((def) => (
        <Col md={6} lg={colSpan} key={def.id}>
          <FeatureTile
            def={def}
            surface={surface}
            highlight={highlights[def.id]}
            actions={resolveActions(def, {
              surface,
              isLoggedIn,
              lang: i18n.language,
              t: translate,
              weeklyVideoUrl,
            })}
          />
        </Col>
      ))}
    </Row>
  );
}
